import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, X, Mic, MicOff, Wind, Hand, Sparkles, Save,
  ArrowRight, Flame, CloudRain, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { key: 'hypomani',   label: 'Det kryper',        emoji: '⚡️', desc: 'Hög energi, rastlös, babblig', color: '#FFE8A3' },
  { key: 'angest',     label: 'Ångest',             emoji: '🌪️', desc: 'Oro som kramar', color: '#A2D2FF' },
  { key: 'iritabel',   label: 'Irritabel',          emoji: '🌋', desc: 'Kort stubin, lättretlig', color: '#FFB3BA' },
  { key: 'depression', label: 'Tungt',              emoji: '🌧️', desc: 'Orkeslös, tomt', color: '#CDB4DB' },
  { key: 'blandad',    label: 'Blandat',            emoji: '🌈', desc: 'Både upp och ner', color: '#FFC8DD' },
  { key: 'utmattning', label: 'Utmattad',           emoji: '🫠', desc: 'Slut, behöver vila', color: '#B9FBC0' },
];

const HYPO_AFFIRMATIONS = [
  'Du är inte jobbig. Din hjärna är i högväxel — det är ett neurologiskt tillstånd, inte ett karaktärsdrag.',
  'Energin behöver någonstans att gå. Du skyddar dig själv genom att kanalisera den.',
  'Det är okej att prata snabbt. Det är okej att inte sitta still. Det är okej att vara DU.',
  'Det här skovet kommer gå över. Det har alltid gjort det. Du överlever.',
  'Du behöver inte "lugna ner dig" för någon annan just nu. Du får bara vara.',
  'Kroppen är smart. Den säger till när den behöver skaka av sig. Lyssna på den utan skam.',
  'Att du vet vad det är — det är ett superkraft. Många hade bara tyckt de blev galna.',
];

const GENERAL_AFFIRMATIONS = [
  'Du gör så gott du kan just nu. Det räcker.',
  'Din värde som människa är inte relaterat till din produktivitet.',
  'Den här känslan är tillfällig. Den kommer passera.',
  'Du är älskad. Precis som du är, precis nu.',
];

/* ---------- STEG 1: Dumpa det ---------- */
function DumpStep({ onNext }) {
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(false);
  const [saved, setSaved] = useState(false);
  const recRef = useRef(null);

  const supported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const toggleDictate = () => {
    if (recording && recRef.current) { recRef.current.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'sv-SE';
    rec.interimResults = true;
    rec.continuous = true;
    let final = text;
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final = (final ? final + ' ' : '') + t;
        else interim = t;
      }
      setText(interim ? `${final} ${interim}`.trim() : final);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    rec.start();
    recRef.current = rec;
    setRecording(true);
  };

  const releaseIt = () => {
    setText('');
    setSaved(false);
    toast.success('💨 Släppt. Bort med det.');
  };

  return (
    <div className="space-y-4" data-testid="episode-dump-step">
      <div className="neo-card p-4 bg-[#FFF7E0]">
        <h3 className="font-bold flex items-center gap-2">
          <Wind className="w-5 h-5" /> Först — få ut det
        </h3>
        <p className="text-xs text-black/75 mt-1 leading-relaxed">
          Prata eller skriv. Snabbt, oredigerat, precis som det kommer ut. Ingen läser det.
          Du kan **släppa det** efteråt (ingen sparar) eller **spara det** i din dagbok om du vill titta tillbaka.
        </p>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Bara skriv allt som snurrar — ingen grammatik, inga punkter, bara ut..."
        rows={8}
        className="neo-input w-full resize-y leading-relaxed"
        style={{ background: '#FFFEF5' }}
        data-testid="episode-dump-text"
        autoFocus
      />
      <div className="flex gap-2">
        {supported && (
          <button
            onClick={toggleDictate}
            className={`neo-btn px-3 py-2 font-bold text-sm flex items-center gap-1 ${recording ? 'bg-red-200 animate-pulse' : 'bg-white'}`}
            data-testid="episode-dump-mic"
          >
            {recording ? <><MicOff className="w-4 h-4" /> Stoppa</> : <><Mic className="w-4 h-4" /> Prata</>}
          </button>
        )}
        <button
          onClick={releaseIt}
          className="neo-btn px-3 py-2 font-bold text-sm bg-[#A2D2FF] flex items-center gap-1"
          data-testid="episode-dump-release"
          disabled={!text.trim()}
        >
          💨 Släpp det
        </button>
        <button
          onClick={async () => {
            if (!text.trim()) { toast.info('Inget att spara just nu'); return; }
            try {
              await axios.post(`${API}/diary/entries`, { text, mode: 'listener' }, { withCredentials: true });
              setSaved(true);
              toast.success('💛 Sparad i Kära dagbok');
            } catch {
              toast.error('Kunde inte spara');
            }
          }}
          disabled={!text.trim() || saved}
          className="neo-btn px-3 py-2 font-bold text-sm bg-[#FFE8A3] flex items-center gap-1 disabled:opacity-60"
          data-testid="episode-dump-save"
        >
          <Save className="w-4 h-4" /> {saved ? 'Sparad' : 'Spara'}
        </button>
      </div>
      <button
        onClick={onNext}
        className="neo-btn w-full py-2 bg-[#C8B6FF] font-bold flex items-center justify-center gap-2"
        data-testid="episode-dump-next"
      >
        Nästa — nu kommer vi ner i kroppen <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ---------- STEG 2: Grounding 5-4-3-2-1 ---------- */
const GROUNDING_STEPS = [
  { sense: '5 saker jag kan SE', icon: '👀', color: '#A2D2FF', count: 5 },
  { sense: '4 saker jag kan KÄNNA på', icon: '🤲', color: '#FFC8DD', count: 4 },
  { sense: '3 saker jag kan HÖRA', icon: '👂', color: '#FFE8A3', count: 3 },
  { sense: '2 saker jag kan LUKTA', icon: '👃', color: '#B9FBC0', count: 2 },
  { sense: '1 sak jag kan SMAKA', icon: '👅', color: '#CDB4DB', count: 1 },
];

function GroundingStep({ onNext }) {
  const [idx, setIdx] = useState(0);
  const cur = GROUNDING_STEPS[idx];
  return (
    <div className="space-y-4" data-testid="episode-grounding-step">
      <div className="neo-card p-4 bg-[#E8F5FF]">
        <h3 className="font-bold flex items-center gap-2">
          <Hand className="w-5 h-5" /> Kom ner i kroppen
        </h3>
        <p className="text-xs text-black/75 mt-1">
          Bryt loopen i hjärnan genom att tvinga in sinnena. Benämn saker — högt eller tyst.
        </p>
      </div>
      <div
        className="neo-card p-6 text-center"
        style={{ background: cur.color }}
      >
        <div className="text-5xl mb-2">{cur.icon}</div>
        <h4 className="text-xl font-bold font-['Fredoka']">{cur.sense}</h4>
        <p className="text-[10px] opacity-60 mt-1">{idx + 1} / {GROUNDING_STEPS.length}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className="neo-btn px-3 py-2 bg-white disabled:opacity-40 flex-1 text-sm font-bold"
        >
          ← Tillbaka
        </button>
        {idx < GROUNDING_STEPS.length - 1 ? (
          <button
            onClick={() => setIdx((i) => i + 1)}
            className="neo-btn px-3 py-2 bg-[#C8B6FF] font-bold flex-1 text-sm"
            data-testid="episode-grounding-next"
          >
            Nästa →
          </button>
        ) : (
          <button
            onClick={onNext}
            className="neo-btn px-3 py-2 bg-[#C8B6FF] font-bold flex-1 text-sm"
            data-testid="episode-grounding-done"
          >
            Klar — bränn av resten <Flame className="inline w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- STEG 3: Bränn av energin ---------- */
const BURN_ACTIVITIES = [
  { name: 'Skaka av händerna', emoji: '🙌', detail: 'Skaka lös handlederna hårt i 30 sek. Som att skaka av vatten.', seconds: 30 },
  { name: 'Hoppa jämfota', emoji: '🐰', detail: '20 hopp på stället. Räkna högt.', seconds: 25 },
  { name: 'Armpress mot vägg', emoji: '💪', detail: 'Tryck handflatorna mot väggen så hårt du orkar i 10 sek. Släpp. Upprepa 3 gånger.', seconds: 45 },
  { name: 'Kallt vatten på handleden', emoji: '❄️', detail: 'Gå till kranen. Spola kallt vatten på insidan av handlederna i 30 sek. Resetar nervsystemet.', seconds: 30 },
  { name: 'Krama något', emoji: '🤗', detail: 'Ta en kudde eller en filt. Krama så hårt du orkar i 15 sek. Släpp. Upprepa.', seconds: 30 },
  { name: 'Dansa ut det', emoji: '💃', detail: 'Sätt på en låt du älskar och dansa vilt i hela den. Ingen ser.', seconds: 60 },
];

function BurnStep({ onNext }) {
  const [chosen, setChosen] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!chosen || remaining <= 0) return;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(intervalRef.current); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [chosen]);

  if (!chosen) {
    return (
      <div className="space-y-3" data-testid="episode-burn-step">
        <div className="neo-card p-4 bg-[#FFE8E8]">
          <h3 className="font-bold flex items-center gap-2">
            <Flame className="w-5 h-5" /> Bränn av energin
          </h3>
          <p className="text-xs text-black/75 mt-1">
            Kroppen skriker efter att få röra på sig. Ge den det. Välj en — eller gör flera.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {BURN_ACTIVITIES.map((a) => (
            <button
              key={a.name}
              onClick={() => { setChosen(a); setRemaining(a.seconds); }}
              className="neo-card p-3 bg-white text-left hover:translate-y-[-1px] transition"
              data-testid="episode-burn-activity"
            >
              <div className="flex items-start gap-2">
                <span className="text-2xl">{a.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">{a.name}</p>
                  <p className="text-[11px] text-black/70 leading-snug">{a.detail}</p>
                  <p className="text-[10px] text-black/50 mt-1">⏱ {a.seconds} sek</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={onNext}
          className="neo-btn w-full py-2 bg-[#FDF0D5] text-sm font-bold"
          data-testid="episode-burn-skip"
        >
          Hoppa över — gå vidare till snälla ord
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="episode-burn-active">
      <div className="neo-card p-6 text-center bg-[#FFE8E8]">
        <div className="text-7xl mb-2 animate-bounce">{chosen.emoji}</div>
        <h4 className="text-xl font-bold font-['Fredoka']">{chosen.name}</h4>
        <p className="text-sm mt-1 leading-relaxed">{chosen.detail}</p>
      </div>
      <div className="neo-card p-4 bg-white text-center">
        <p className="text-4xl font-bold font-['Fredoka']" data-testid="episode-burn-timer">{remaining}s</p>
        <div className="h-2 mt-2 bg-black/10 rounded-full overflow-hidden border border-black">
          <div
            className="h-full bg-gradient-to-r from-[#FFB3BA] to-[#FFC8DD] transition-all"
            style={{ width: `${chosen.seconds ? ((chosen.seconds - remaining) / chosen.seconds) * 100 : 100}%` }}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => { setChosen(null); setRemaining(0); }}
          className="neo-btn flex-1 py-2 bg-white text-sm font-bold"
        >
          Byt aktivitet
        </button>
        <button
          onClick={onNext}
          className="neo-btn flex-1 py-2 bg-[#C8B6FF] text-sm font-bold flex items-center justify-center gap-1"
          data-testid="episode-burn-next"
        >
          Nästa — snälla ord <Sparkles className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* ---------- STEG 4: Snälla ord ---------- */
function AffirmationsStep({ kind, onDone }) {
  const pool = kind === 'hypomani' ? HYPO_AFFIRMATIONS : GENERAL_AFFIRMATIONS;
  const [idx, setIdx] = useState(0);

  const speak = (text) => {
    if (!('speechSynthesis' in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'sv-SE';
    u.rate = 0.95;
    u.pitch = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  return (
    <div className="space-y-3" data-testid="episode-affirm-step">
      <div className="neo-card p-4 bg-[#FFE8F5]">
        <h3 className="font-bold flex items-center gap-2">
          <Heart className="w-5 h-5 fill-[#FF8FA3] text-[#FF8FA3]" /> Hör något snällt
        </h3>
        <p className="text-xs text-black/75 mt-1">
          Klicka på högtalaren om du vill att det ska läsas upp. Stanna på varje så länge du behöver.
        </p>
      </div>
      <div
        className="neo-card p-6 text-center"
        style={{ background: 'linear-gradient(135deg,#FFE8F5,#FFF7E0)' }}
      >
        <p className="text-lg leading-relaxed font-['Fredoka']" data-testid="episode-affirm-text">
          {pool[idx]}
        </p>
        <button
          onClick={() => speak(pool[idx])}
          className="neo-btn mt-3 px-3 py-1.5 bg-white text-sm font-bold"
          data-testid="episode-affirm-speak"
        >
          🔊 Läs upp
        </button>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setIdx((i) => (i - 1 + pool.length) % pool.length)}
          className="neo-btn flex-1 py-2 bg-white text-sm font-bold"
        >
          ← Föregående
        </button>
        <button
          onClick={() => setIdx((i) => (i + 1) % pool.length)}
          className="neo-btn flex-1 py-2 bg-white text-sm font-bold"
          data-testid="episode-affirm-next"
        >
          Nästa →
        </button>
      </div>
      <button
        onClick={onDone}
        className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold"
        data-testid="episode-done"
      >
        ✨ Klar. Tack för att jag tog hand om mig.
      </button>
    </div>
  );
}

/* ---------- MAIN EPISODE SUPPORT MODAL ---------- */
export default function EpisodeSupport({ open, onClose }) {
  const [kind, setKind] = useState(null);
  const [intensity, setIntensity] = useState(3);
  const [step, setStep] = useState('pick'); // pick | dump | grounding | burn | affirm | done
  const [logged, setLogged] = useState(false);

  useEffect(() => {
    if (open) {
      setKind(null);
      setIntensity(3);
      setStep('pick');
      setLogged(false);
    }
  }, [open]);

  const logEpisode = async () => {
    if (!kind || logged) return;
    try {
      await axios.post(`${API}/episodes`, { kind, intensity }, { withCredentials: true });
      setLogged(true);
    } catch { /* silent */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      data-testid="episode-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg my-6 neo-card bg-white p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-['Fredoka'] font-bold text-xl flex items-center gap-2">
            <Heart className="w-5 h-5 fill-[#FF8FA3] text-[#FF8FA3]" /> När det kryper
          </h2>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Stäng" data-testid="episode-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 'pick' && (
          <div className="space-y-3" data-testid="episode-pick-step">
            <div className="neo-card p-3 bg-[#FFE8F5]">
              <p className="text-sm leading-relaxed">
                Hej 💛 Du är här för att du märkte något. Det är redan ett superkraft.
                <br />
                <span className="text-xs opacity-75">Berätta vad som känns just nu så ger jag dig rätt verktyg.</span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  onClick={() => setKind(k.key)}
                  className={`neo-card p-3 text-left transition ${kind === k.key ? 'translate-y-[-2px]' : ''}`}
                  style={{ background: kind === k.key ? k.color : 'white' }}
                  data-testid={`episode-kind-${k.key}`}
                >
                  <div className="text-2xl">{k.emoji}</div>
                  <p className="font-bold text-sm mt-0.5">{k.label}</p>
                  <p className="text-[10px] text-black/70 leading-tight">{k.desc}</p>
                </button>
              ))}
            </div>
            {kind && (
              <>
                <div>
                  <p className="text-xs font-bold mb-1">Hur starkt känns det? ({intensity}/5)</p>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={intensity}
                    onChange={(e) => setIntensity(Number(e.target.value))}
                    className="w-full accent-[#FF8FA3]"
                    data-testid="episode-intensity"
                  />
                  <div className="flex justify-between text-[10px] opacity-60">
                    <span>Lite</span><span>Normalt</span><span>Mycket</span>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await logEpisode();
                    setStep(kind === 'hypomani' || kind === 'iritabel' ? 'dump' : 'grounding');
                  }}
                  className="neo-btn w-full py-3 bg-gradient-to-br from-[#FFC8DD] to-[#C8B6FF] font-bold"
                  data-testid="episode-start"
                >
                  Kör — hjälp mig nu 💛
                </button>
              </>
            )}
          </div>
        )}

        {step === 'dump' && <DumpStep onNext={() => setStep('grounding')} />}
        {step === 'grounding' && <GroundingStep onNext={() => setStep('burn')} />}
        {step === 'burn' && <BurnStep onNext={() => setStep('affirm')} />}
        {step === 'affirm' && <AffirmationsStep kind={kind} onDone={() => setStep('done')} />}

        {step === 'done' && (
          <div className="space-y-3 text-center" data-testid="episode-done-step">
            <div className="text-6xl">💛</div>
            <h3 className="font-bold text-xl font-['Fredoka']">Du tog hand om dig.</h3>
            <p className="text-sm leading-relaxed">
              Det här skovet är loggat (för din egen överblick) och du gjorde hela vägen.
              Det är inte lite. Det är mycket.
            </p>
            <button onClick={onClose} className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold">
              Okej, stäng
            </button>
          </div>
        )}

        {/* Crisis footer — always visible */}
        {step !== 'done' && (
          <div className="mt-3 text-[10px] text-center text-black/60 leading-relaxed">
            💛 Om tankarna blir mörka — <a href="tel:112" className="underline font-bold">112</a>,{' '}
            <a href="tel:1177" className="underline font-bold">1177</a>,{' '}
            <a href="tel:90101" className="underline font-bold">Mind 90101</a>
          </div>
        )}
      </div>
    </div>
  );
}
